import React, { useState } from "react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CrossIcon,
  DownloadIcon,
} from "@100mslive/react-icons";
import {
  Box,
  Dropdown,
  Flex,
  IconButton,
  Text,
  textEllipsis,
} from "@100mslive/roomkit-react";
import {
  selectHMSMessages,
  selectLocalPeerRoleName,
  useHMSStore,
} from "@100mslive/react-sdk";
import { ChatSelector } from "./ChatSelector";
import { useSidepaneToggle } from "../AppData/useSidepane";
import { SIDE_PANE_OPTIONS } from "../../common/constants";
import { exportChatMessages } from "./utils/chatExport";
import { ToastManager } from "../Toast/ToastManager";

export const ChatHeader = React.memo(
  ({ selection, selectorOpen, onToggle, onSelect, role, peerId }) => {
    const [open, setOpen] = useState(false);
    const toggleChat = useSidepaneToggle(SIDE_PANE_OPTIONS.CHAT);
    const localPeerRoleName = useHMSStore(selectLocalPeerRoleName);
    const allMessages = useHMSStore(selectHMSMessages) || [];
    const isBroadcaster = localPeerRoleName === "broadcaster";

    const handleExportChat = e => {
      e.stopPropagation();
      try {
        if (allMessages.length === 0) {
          ToastManager.addToast({
            title: "No messages to export",
          });
          return;
        }
        exportChatMessages(allMessages);
        ToastManager.addToast({
          title: "Chat exported successfully",
        });
      } catch (error) {
        ToastManager.addToast({
          title: `Failed to export chat: ${error.message}`,
        });
      }
    };

    return (
      <Flex
        onClick={onToggle}
        align="center"
        css={{
          color: "$on_surface_high",
          h: "$16",
          mb: "$2",
        }}
      >
        <Text variant="h6">Chat </Text>
        <Dropdown.Root open={open} onOpenChange={value => setOpen(value)}>
          <Dropdown.Trigger
            asChild
            data-testid="participant_list_filter"
            css={{
              border: "1px solid $on_surface_low",
              r: "$0",
              p: "$2 $4",
              ml: "$8",
            }}
            tabIndex={0}
          >
            <Flex align="center">
              <Text variant="sm" css={{ ...textEllipsis(80) }}>
                {selection}
              </Text>
              <Box css={{ ml: "$2", color: "$on_surface_low" }}>
                {open ? (
                  <ChevronUpIcon width={14} height={14} />
                ) : (
                  <ChevronDownIcon width={14} height={14} />
                )}
              </Box>
            </Flex>
          </Dropdown.Trigger>
          <Dropdown.Content
            css={{
              w: "$64",
              overflow: "hidden",
              maxHeight: "unset",
              backgroundColor: "$surface_dim",
            }}
            align="start"
            sideOffset={8}
          >
            <ChatSelector onSelect={onSelect} role={role} peerId={peerId} />
          </Dropdown.Content>
        </Dropdown.Root>
        {isBroadcaster && (
          <IconButton
            css={{ ml: "auto", mr: "$4" }}
            onClick={handleExportChat}
            data-testid="export_chat"
            title="Export Chat"
          >
            <DownloadIcon />
          </IconButton>
        )}
        <IconButton
          css={{ ml: isBroadcaster ? 0 : "auto" }}
          onClick={e => {
            e.stopPropagation();
            selectorOpen ? onToggle() : toggleChat();
          }}
          data-testid="close_chat"
        >
          <CrossIcon />
        </IconButton>
      </Flex>
    );
  }
);
